import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { db, storage } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import FamilyCirclePanel from "../components/CareTeam/FamilyCirclePanel";
import {
  FaUserEdit,
  FaArrowLeft,
  FaUser,
  FaPhoneAlt,
  FaHeartbeat,
  FaStickyNote,
  FaCamera,
  FaTrash,
} from "react-icons/fa";

const API_BASE_URL = `https://asia-southeast1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api`;

const CareProfileDetailsPage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const {
    currentUser,
    ownedProfiles,
    sharedProfiles,
    profilesLoading,
    updateCareProfile,
  } = useAuth();

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoMenuOpen, setPhotoMenuOpen] = useState(false);
  const [isPhotoUpdating, setIsPhotoUpdating] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [photoError, setPhotoError] = useState("");
  const [photoStatus, setPhotoStatus] = useState("");
  const [photoPreviewURL, setPhotoPreviewURL] = useState("");
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [pendingPhotoPreviewURL, setPendingPhotoPreviewURL] = useState("");
  const [pendingPhotoDimensions, setPendingPhotoDimensions] = useState(null);
  const [cropSettings, setCropSettings] = useState({ x: 0, y: 0, zoom: 1 });
  const fileInputRef = useRef(null);
  const localPreviewUrlRef = useRef(null);
  const dragStateRef = useRef(null);

  useEffect(() => {
    if (!profileId || !currentUser) {
      setLoading(false);
      return;
    }

    if (profilesLoading) {
      setLoading(true);
      return;
    }

    const fetchProfile = async () => {
      setLoading(true);
      try {
        const loadSharedRelationship = async (baseProfile) => {
          if (baseProfile.userId === currentUser.uid) {
            return baseProfile;
          }

          const accessibleProfileRef = doc(
            db,
            "users",
            currentUser.uid,
            "accessibleProfiles",
            baseProfile.id,
          );
          const memberDocRef = doc(
            db,
            "careProfiles",
            baseProfile.id,
            "careTeamMembers",
            currentUser.uid,
          );
          const [accessibleProfileSnap, memberSnap] = await Promise.all([
            getDoc(accessibleProfileRef),
            getDoc(memberDocRef),
          ]);
          const directRelationship =
            accessibleProfileSnap.data()?.relationship ||
            memberSnap.data()?.relationship ||
            baseProfile.relationship;
          let resolvedRelationship = directRelationship;

          if (!resolvedRelationship) {
            const response = await fetch(
              `${API_BASE_URL}/care-team/resolve-shared-relationship`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${await currentUser.getIdToken()}`,
                },
                body: JSON.stringify({ careProfileId: baseProfile.id }),
              },
            );

            if (response.ok) {
              const result = await response.json();
              resolvedRelationship = result.relationship || null;
            }
          }

          return {
            ...baseProfile,
            relationship: resolvedRelationship,
          };
        };

        const knownProfile = [...ownedProfiles, ...sharedProfiles].find(
          (profile) => profile.id === profileId,
        );

        if (knownProfile) {
          setProfileData(await loadSharedRelationship(knownProfile));
          setLoading(false);
          return;
        }

        const profileDocRef = doc(db, "careProfiles", profileId);
        const docSnap = await getDoc(profileDocRef);
        if (docSnap.exists() && docSnap.data().userId === currentUser.uid) {
          setProfileData({ id: docSnap.id, ...docSnap.data() });
        } else {
          console.error("No such profile or unauthorized");
          navigate("/profiles");
        }
      } catch (error) {
        console.error("Error fetching profile details:", error);
        navigate("/profiles");
      }
      setLoading(false);
    };

    fetchProfile();
  }, [
    currentUser,
    navigate,
    ownedProfiles,
    profileId,
    profilesLoading,
    sharedProfiles,
  ]);

  useEffect(() => {
    if (!loading && window.location.hash === "#family-circle") {
      document
        .getElementById("family-circle")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading]);

  useEffect(() => {
    setPhotoPreviewURL(profileData?.profilePictureURL || "");
  }, [profileData?.profilePictureURL]);

  useEffect(() => {
    return () => {
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
      }
    };
  }, []);

  const DetailItem = ({ label, value }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
        {value || "N/A"}
      </dd>
    </div>
  );

  const Section = ({ title, icon, children }) => (
    <div className="mb-8">
      <div className="flex items-center mb-4">
        <div className="bg-blue-100 text-blue-600 rounded-lg p-2 mr-4">
          {icon}
        </div>
        <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
      </div>
      <div className="bg-white shadow-md rounded-lg border border-gray-200">
        <dl className="divide-y divide-gray-200 px-6">{children}</dl>
      </div>
    </div>
  );

  const formatAddress = (address) => {
    if (!address) return null;
    const parts = [
      address.street,
      address.city,
      address.state,
      address.zip,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  const updateProfilePhoto = async (photoUpdates) => {
    await updateCareProfile(profileId, photoUpdates);
    setProfileData((prev) => (prev ? { ...prev, ...photoUpdates } : prev));
  };

  const createCroppedProfilePhoto = (file, crop) =>
    new Promise((resolve, reject) => {
      const imageUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        URL.revokeObjectURL(imageUrl);

        const outputSize = 512;
        const previewSize = 192;
        const baseScale = Math.min(
          outputSize / image.width,
          outputSize / image.height,
        );
        const scale = baseScale * crop.zoom;
        const drawnWidth = image.width * scale;
        const drawnHeight = image.height * scale;
        const offsetX = (crop.x / previewSize) * outputSize;
        const offsetY = (crop.y / previewSize) * outputSize;
        const canvas = document.createElement("canvas");
        canvas.width = outputSize;
        canvas.height = outputSize;

        const context = canvas.getContext("2d");
        if (!context) {
          reject(new Error("Unable to prepare this image for upload."));
          return;
        }

        context.fillStyle = "#f3f4f6";
        context.fillRect(0, 0, outputSize, outputSize);
        context.drawImage(
          image,
          (outputSize - drawnWidth) / 2 + offsetX,
          (outputSize - drawnHeight) / 2 + offsetY,
          drawnWidth,
          drawnHeight,
        );
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Unable to compress this image."));
              return;
            }

            const compressedFile = new File([blob], "profile-photo.jpg", {
              type: blob.type,
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          "image/jpeg",
          0.82,
        );
      };

      image.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error("Unable to read this image file."));
      };

      image.src = imageUrl;
    });

  const uploadProfileImage = (imageRef, file) =>
    new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        reject(new Error("The upload took too long. Please try again."));
      }, 45000);

      uploadBytes(imageRef, file, { contentType: file.type })
        .then(async (snapshot) => {
          window.clearTimeout(timeoutId);
          setUploadProgress(100);
          return getDownloadURL(snapshot.ref);
        })
        .then(resolve)
        .catch((error) => {
          window.clearTimeout(timeoutId);
          reject(error);
        });
    });

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setPhotoError("Please select a valid image file.");
      return;
    }

    setPhotoMenuOpen(false);
    setUploadProgress(0);
    setPhotoError("");
    setPhotoStatus("");
    setCropSettings({ x: 0, y: 0, zoom: 1 });

    if (localPreviewUrlRef.current) {
      URL.revokeObjectURL(localPreviewUrlRef.current);
    }
    localPreviewUrlRef.current = URL.createObjectURL(file);
    setPendingPhotoFile(file);
    setPendingPhotoPreviewURL(localPreviewUrlRef.current);
    setPendingPhotoDimensions(null);
  };

  const editExistingPhoto = async () => {
    if (!profileData?.profilePictureURL) return;

    setPhotoMenuOpen(false);
    setUploadProgress(0);
    setPhotoError("");
    setPhotoStatus("");
    setCropSettings({ x: 0, y: 0, zoom: 1 });

    if (localPreviewUrlRef.current) {
      URL.revokeObjectURL(localPreviewUrlRef.current);
      localPreviewUrlRef.current = null;
    }

    try {
      const response = await fetch(profileData.profilePictureURL);
      if (!response.ok) {
        throw new Error("Unable to load the current profile photo.");
      }

      const blob = await response.blob();
      const file = new File([blob], "profile-photo.jpg", {
        type: blob.type || "image/jpeg",
        lastModified: Date.now(),
      });

      localPreviewUrlRef.current = URL.createObjectURL(file);
      setPendingPhotoFile(file);
      setPendingPhotoPreviewURL(localPreviewUrlRef.current);
      setPendingPhotoDimensions(null);
    } catch (error) {
      console.error("Profile photo edit failed:", error);
      setPhotoError(error.message || "Unable to edit the current photo.");
    }
  };

  const cancelPhotoCrop = () => {
    if (localPreviewUrlRef.current) {
      URL.revokeObjectURL(localPreviewUrlRef.current);
      localPreviewUrlRef.current = null;
    }
    setPendingPhotoFile(null);
    setPendingPhotoPreviewURL("");
    setPendingPhotoDimensions(null);
    setCropSettings({ x: 0, y: 0, zoom: 1 });
  };

  const startCropDrag = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: cropSettings.x,
      originY: cropSettings.y,
    };
  };

  const moveCropDrag = (event) => {
    if (!dragStateRef.current) return;

    const { startX, startY, originX, originY } = dragStateRef.current;
    setCropSettings((prev) => ({
      ...prev,
      x: originX + event.clientX - startX,
      y: originY + event.clientY - startY,
    }));
  };

  const endCropDrag = (event) => {
    if (dragStateRef.current?.pointerId === event.pointerId) {
      event.currentTarget.releasePointerCapture(event.pointerId);
      dragStateRef.current = null;
    }
  };

  const uploadCroppedPhoto = async () => {
    if (!pendingPhotoFile) return;

    setIsPhotoUpdating(true);
    setUploadProgress(0);
    setPhotoError("");
    setPhotoStatus("Creating profile photo...");
    setPhotoPreviewURL(pendingPhotoPreviewURL);

    let uploadedStoragePath = null;

    try {
      const previousStoragePath =
        profileData?.profilePictureStoragePath || null;
      const compressedFile = await createCroppedProfilePhoto(
        pendingPhotoFile,
        cropSettings,
      );
      const safeFileName = compressedFile.name.replace(/[^\w.-]/g, "_");
      const newStoragePath = `profilePictures/${currentUser.uid}/${profileId}/${Date.now()}-${safeFileName}`;
      const imageRef = ref(storage, newStoragePath);

      setPhotoStatus("Uploading photo...");
      const downloadURL = await uploadProfileImage(imageRef, compressedFile);
      uploadedStoragePath = newStoragePath;
      await updateProfilePhoto({
        profilePictureURL: downloadURL,
        profilePictureStoragePath: newStoragePath,
      });
      setPhotoPreviewURL(downloadURL);
      cancelPhotoCrop();

      if (previousStoragePath && previousStoragePath !== newStoragePath) {
        deleteObject(ref(storage, previousStoragePath)).catch((error) => {
          console.warn("Failed to delete previous profile photo:", error);
        });
      }
    } catch (error) {
      console.error("Profile photo upload failed:", error);
      if (uploadedStoragePath) {
        deleteObject(ref(storage, uploadedStoragePath)).catch((deleteError) => {
          console.warn(
            "Failed to clean up uploaded profile photo:",
            deleteError,
          );
        });
      }
      setPhotoError(error.message || "Unable to update the profile photo.");
      cancelPhotoCrop();
      setPhotoPreviewURL(profileData?.profilePictureURL || "");
    } finally {
      setIsPhotoUpdating(false);
      setUploadProgress(0);
      setPhotoStatus("");
    }
  };

  const handlePhotoDelete = async () => {
    if (!profileData?.profilePictureURL) return;

    const shouldDelete = window.confirm(
      "Remove this profile photo? You can upload a new one anytime.",
    );
    if (!shouldDelete) return;

    setPhotoMenuOpen(false);
    setIsPhotoUpdating(true);
    setPhotoError("");
    setPhotoStatus("Removing photo...");

    const previousStoragePath = profileData.profilePictureStoragePath || null;

    try {
      await updateProfilePhoto({
        profilePictureURL: null,
        profilePictureStoragePath: null,
      });
      if (localPreviewUrlRef.current) {
        URL.revokeObjectURL(localPreviewUrlRef.current);
        localPreviewUrlRef.current = null;
      }
      setPhotoPreviewURL("");

      if (previousStoragePath) {
        deleteObject(ref(storage, previousStoragePath)).catch((error) => {
          console.warn("Failed to delete profile photo from storage:", error);
        });
      }
    } catch (error) {
      console.error("Profile photo deletion failed:", error);
      setPhotoError(error.message || "Unable to delete the profile photo.");
    } finally {
      setIsPhotoUpdating(false);
      setPhotoStatus("");
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-600">
        Loading profile details...
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="p-6 text-center text-red-600">
        <p>Could not load profile details.</p>
        <button
          onClick={() => navigate("/profiles")}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Back to Profiles
        </button>
      </div>
    );
  }

  const {
    profileName,
    dateOfBirth,
    address,
    contactNumber,
    gender,
    relationshipToUser,
    emergencyContactName,
    emergencyContactPhone,
    emergencyContactRelationship,
    primaryCaregiver,
    bloodType,
    allergiesAndAdverseReactions,
    medicalConditions,
    significantMedicalHistory,
    insuranceProvider,
    preferredHospital,
    preferredLanguage,
    primaryDoctor,
    notes,
  } = profileData;
  const isOwner = profileData.userId === currentUser?.uid;
  const displayedRelationshipToUser = isOwner
    ? relationshipToUser
    : profileData.relationship;
  const cropPreviewSize = 192;
  const cropPreviewScale = pendingPhotoDimensions
    ? Math.min(
        cropPreviewSize / pendingPhotoDimensions.width,
        cropPreviewSize / pendingPhotoDimensions.height,
      ) * cropSettings.zoom
    : 1;
  const cropPreviewStyle = pendingPhotoDimensions
    ? {
        height: `${pendingPhotoDimensions.height * cropPreviewScale}px`,
        left: `calc(50% + ${cropSettings.x}px)`,
        top: `calc(50% + ${cropSettings.y}px)`,
        transform: "translate(-50%, -50%)",
        width: `${pendingPhotoDimensions.width * cropPreviewScale}px`,
      }
    : {};

  return (
    <div className="p-4 md:p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={() => navigate("/profiles")}
            className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-800"
          >
            <FaArrowLeft className="mr-2" /> Back to Profiles
          </button>
          {isOwner && (
            <Link
              to={`/profiles/edit/${profileId}`}
              className="flex min-h-11 items-center px-4 py-2 bg-yellow-400 text-white font-semibold rounded-lg shadow-md hover:bg-yellow-500 transition duration-150 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2"
            >
              <FaUserEdit className="mr-2" /> Edit Profile
            </Link>
          )}
        </div>

        <div className="mb-8">
          <div className="flex items-center justify-center gap-4">
            <div className="relative">
              <button
                type="button"
                onClick={() => isOwner && setPhotoMenuOpen((open) => !open)}
                disabled={!isOwner || isPhotoUpdating}
                className={`relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-4 bg-white shadow-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                  isOwner
                    ? "cursor-pointer border-blue-200 hover:border-blue-300"
                    : "cursor-default border-gray-200"
                }`}
                aria-label={isOwner ? "Manage profile photo" : "Profile photo"}
              >
                {photoPreviewURL ? (
                  <img
                    src={photoPreviewURL}
                    alt={profileName || "Profile"}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <FaUser className="text-3xl text-gray-400" />
                )}
                {isOwner && (
                  <span className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-slate-600 text-white shadow-sm">
                    <FaCamera className="text-[10px]" />
                  </span>
                )}
              </button>

              {isOwner && photoMenuOpen && (
                <div className="absolute left-0 z-10 mt-2 w-48 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 text-left shadow-lg">
                  {profileData.profilePictureURL ? (
                    <>
                      <button
                        type="button"
                        onClick={editExistingPhoto}
                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <FaCamera className="mr-2 text-gray-400" />
                        Edit photo
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPhotoMenuOpen(false);
                          fileInputRef.current?.click();
                        }}
                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <FaCamera className="mr-2 text-gray-400" />
                        Upload new photo
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setPhotoMenuOpen(false);
                        fileInputRef.current?.click();
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <FaCamera className="mr-2 text-gray-400" />
                      Upload photo
                    </button>
                  )}
                  {profileData.profilePictureURL && (
                    <button
                      type="button"
                      onClick={handlePhotoDelete}
                      className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <FaTrash className="mr-2 text-red-400" />
                      Delete photo
                    </button>
                  )}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>

            <div className="text-left">
              <h1 className="text-3xl font-bold text-gray-800">
                {profileName}
              </h1>
              <p className="text-md text-gray-500">
                Personal and Medical Details
              </p>
            </div>
          </div>
          {(isPhotoUpdating || photoError) && (
            <div className="mx-auto mt-4 max-w-sm rounded-lg border border-gray-200 bg-white p-3 text-center shadow-sm">
              {uploadProgress > 0 && (
                <div className="mb-2 h-2 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full rounded-full bg-blue-600 transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}
              <p
                className={`text-xs ${
                  photoError ? "text-red-600" : "text-gray-500"
                }`}
              >
                {photoError ||
                  (uploadProgress > 0
                    ? `${Math.round(uploadProgress)}% uploaded`
                    : photoStatus || "Preparing photo upload...")}
              </p>
              {photoError && (
                <button
                  type="button"
                  onClick={() => {
                    setPhotoError("");
                    fileInputRef.current?.click();
                  }}
                  className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-700"
                >
                  Try again
                </button>
              )}
            </div>
          )}
          <div className="mt-3 flex justify-center">
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                isOwner
                  ? "bg-green-100 text-green-700"
                  : "bg-indigo-100 text-indigo-700"
              }`}
            >
              {isOwner ? "My Profiles" : "Shared with me"}
              {!isOwner && profileData.role ? ` - ${profileData.role}` : ""}
            </span>
          </div>
        </div>

        {pendingPhotoPreviewURL && !isPhotoUpdating && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
              <h2 className="text-xl font-semibold text-gray-800">
                Position Profile Photo
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Drag the photo to center it, then adjust zoom if needed.
              </p>

              <div className="mt-6 flex justify-center">
                <div
                  className="relative h-48 w-48 touch-none overflow-hidden rounded-full border-4 border-blue-200 bg-gray-100 shadow-inner"
                  onPointerDown={startCropDrag}
                  onPointerMove={moveCropDrag}
                  onPointerUp={endCropDrag}
                  onPointerCancel={endCropDrag}
                  role="presentation"
                >
                  <img
                    src={pendingPhotoPreviewURL}
                    alt="Profile crop preview"
                    draggable="false"
                    onLoad={(event) =>
                      setPendingPhotoDimensions({
                        height: event.currentTarget.naturalHeight,
                        width: event.currentTarget.naturalWidth,
                      })
                    }
                    className="absolute max-w-none cursor-move select-none"
                    style={cropPreviewStyle}
                  />
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <p className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
                  Click and drag the image inside the circle to move it.
                </p>

                <label className="block text-sm font-medium text-gray-700">
                  Zoom
                  <input
                    type="range"
                    min="1"
                    max="2.5"
                    step="0.05"
                    value={cropSettings.zoom}
                    onChange={(event) =>
                      setCropSettings((prev) => ({
                        ...prev,
                        zoom: Number(event.target.value),
                      }))
                    }
                    className="mt-2 w-full"
                  />
                </label>
              </div>

              <div className="mt-6 flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setCropSettings({ x: 0, y: 0, zoom: 1 })}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={cancelPhotoCrop}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={uploadCroppedPhoto}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
                >
                  Upload Photo
                </button>
              </div>
            </div>
          </div>
        )}

        <FamilyCirclePanel profile={profileData} />

        <Section title="Personal Information" icon={<FaUser size={20} />}>
          <DetailItem
            label="Date of Birth"
            value={
              dateOfBirth?.toDate
                ? dateOfBirth.toDate().toLocaleDateString()
                : "N/A"
            }
          />
          <DetailItem label="Gender" value={gender} />
          <DetailItem
            label="Relationship to you"
            value={displayedRelationshipToUser}
          />
          <DetailItem label="Contact Number" value={contactNumber} />
          <DetailItem label="Address" value={formatAddress(address)} />
          <DetailItem label="Preferred Language" value={preferredLanguage} />
        </Section>

        <Section title="Key Contacts" icon={<FaPhoneAlt size={20} />}>
          <DetailItem label="Primary Support Person" value={primaryCaregiver} />
          <DetailItem
            label="Emergency Contact Name"
            value={emergencyContactName}
          />
          <DetailItem
            label="Emergency Contact Phone"
            value={emergencyContactPhone}
          />
          <DetailItem
            label="Emergency Contact Relationship"
            value={emergencyContactRelationship}
          />
        </Section>

        <Section title="Medical Information" icon={<FaHeartbeat size={20} />}>
          <DetailItem label="Blood Type" value={bloodType} />
          <DetailItem label="Allergies" value={allergiesAndAdverseReactions} />
          <DetailItem label="Medical Conditions" value={medicalConditions} />
          <DetailItem
            label="Medical History"
            value={significantMedicalHistory}
          />
          <DetailItem label="Insurance Provider" value={insuranceProvider} />
          <DetailItem label="Preferred Hospital" value={preferredHospital} />
          <DetailItem label="Primary Doctor" value={primaryDoctor} />
        </Section>

        <Section title="Notes" icon={<FaStickyNote size={20} />}>
          <div className="py-4">
            <dd className="text-sm text-gray-900 whitespace-pre-wrap">
              {notes || "No additional notes."}
            </dd>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default CareProfileDetailsPage;
